import {
  AppstoreOutlined, AuditOutlined, BellOutlined, BookOutlined, CalculatorOutlined, CheckSquareOutlined,
  FileSearchOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  MenuOutlined, ProjectOutlined, RobotOutlined, SettingOutlined, TeamOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Badge, Button, Dropdown, Flex, Grid, Layout, Menu, Space, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { FloatingAiAssistant } from './FloatingAiAssistant';

const { Header, Sider, Content } = Layout;

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: '超级管理员', ORG_ADMIN: '组织管理员', TENDER_MANAGER: '招标项目负责人', TENDER_AGENT: '招标代理专员',
  REVIEWER: '审核人员', EVALUATOR: '评标专家', CHIEF_SUPERVISOR: '总监理工程师', SUPERVISOR: '专业监理工程师',
  DOCUMENT_CLERK: '资料员', OWNER_REP: '建设单位代表', CONTRACTOR: '施工单位用户', AUDITOR: '审计人员', VIEWER: '只读用户',
};
const iconMap: Record<string, React.ReactNode> = { dashboard: <AppstoreOutlined />, project: <ProjectOutlined />, task: <CheckSquareOutlined />, calculator: <CalculatorOutlined />, robot: <RobotOutlined />, list: <UnorderedListOutlined />, team: <TeamOutlined />, audit: <AuditOutlined />, setting: <SettingOutlined />, menu: <MenuOutlined /> };

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organizationName, logout, has } = useAuth();
  const { data: notifications = [] } = useQuery({ queryKey: ['notifications'], queryFn: async () => (await api.get('/collaboration/notifications')).data, refetchInterval: 30000 });
  const { data: navigation = [] } = useQuery<any[]>({ queryKey: ['navigation-menus'], queryFn: async () => (await api.get('/menus/navigation')).data });
  const unread = notifications.filter((item: { readAt?: string }) => !item.readAt).length;

  useEffect(() => {
    if (typeof screens.lg === 'boolean') setCollapsed(!screens.lg);
  }, [screens.lg]);

  const fallbackItems = useMemo(() => [
    { key: '/dashboard', icon: <AppstoreOutlined />, label: '总览' },
    { key: '/projects', icon: <ProjectOutlined />, label: '项目中心' },
    { key: '/workbench', icon: <CheckSquareOutlined />, label: '我的工作台' },
    { key: '/calculators', icon: <CalculatorOutlined />, label: '计算中心' },
    { key: '/ai', icon: <RobotOutlined />, label: 'AI 业务助手' },
    ...(has('ai.use') ? [{ key: '/ai-jobs', icon: <UnorderedListOutlined />, label: 'AI 任务记录' }] : []),
    ...(has('user.read') ? [{ key: '/users', icon: <TeamOutlined />, label: '组织与权限' }] : []),
    ...(has('audit.read') ? [{ key: '/audit', icon: <AuditOutlined />, label: '审计日志' }] : []),
    ...(has('ai.config') ? [{ key: '/settings', icon: <SettingOutlined />, label: '系统设置' }] : []),
    ...(has('menu.manage') ? [{ key: '/menu-management', icon: <MenuOutlined />, label: '菜单管理' }] : []),
  ], [has]);
  const menuItems = useMemo(() => {
    if (!navigation.length) return fallbackItems;
    const convert = (items: any[]): any[] => items.map((item) => ({
      key: item.type === 'GROUP' ? `group:${item.id}` : item.type === 'EXTERNAL_EMBED' ? `/external/${item.id}` : item.type === 'EXTERNAL_OPEN' ? `external:${item.id}` : item.route,
      icon: iconMap[item.icon] ?? <MenuOutlined />,
      label: item.name,
      children: item.children?.length ? convert(item.children) : undefined,
      externalUrl: item.externalUrl,
    }));
    return convert(navigation);
  }, [navigation, fallbackItems]);

  const flatMenuItems = useMemo(() => { const output: any[] = []; const walk = (items: any[]) => items.forEach((item) => { output.push(item); if (item.children) walk(item.children); }); walk(menuItems); return output; }, [menuItems]);
  const selected = flatMenuItems.find((item) => item.key !== '/dashboard' && (location.pathname === item.key || location.pathname.startsWith(`${item.key}/`)))?.key ?? '/dashboard';
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={232} collapsedWidth={72} collapsed={collapsed} theme="dark" style={{ position: 'fixed', inset: '0 auto 0 0', zIndex: 10 }}>
        <Flex align="center" gap={10} style={{ height: 68, padding: collapsed ? '0 17px' : '0 20px', overflow: 'hidden' }}>
          <span className="brand-mark">筑</span>
          {!collapsed && <div style={{ whiteSpace: 'nowrap' }}><div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>筑衡智管</div><div style={{ color: '#8fa5d1', fontSize: 11 }}>PROJECT INTELLIGENCE</div></div>}
        </Flex>
        <Menu theme="dark" mode="inline" selectedKeys={[selected]} items={menuItems} onClick={({ key }) => { if (String(key).startsWith('group:')) return; if (String(key).startsWith('external:')) { const id = String(key).slice(9); const stack = [...navigation]; let found: any; while (stack.length) { const current = stack.shift(); if (current.id === id) { found = current; break; } stack.push(...(current.children ?? [])); } if (found?.externalUrl) window.open(found.externalUrl, '_blank', 'noopener,noreferrer'); return; } navigate(String(key)); }} style={{ borderInlineEnd: 0, padding: '8px 10px' }} />
        {!collapsed && <div style={{ position: 'absolute', left: 18, right: 18, bottom: 20, padding: 14, color: '#9db0d7', fontSize: 12, border: '1px solid rgba(255,255,255,.08)', borderRadius: 10 }}><BookOutlined /> 数据可追溯 · AI 有依据<br /><span style={{ opacity: .65 }}>v1.0.0</span></div>}
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 72 : 232, transition: 'margin .2s' }}>
        <Header className="app-header">
          <Space size={12} className="org-context">
            <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed(!collapsed)} />
            <div className="org-context-copy"><div className="org-context-name">{organizationName}</div><div className="org-context-meta">工程项目协同工作空间</div></div>
          </Space>
          <Space size={18}>
            <Dropdown trigger={['click']} menu={{ items: notifications.slice(0, 8).map((n: { id: string; title: string; readAt?: string }) => ({ key: n.id, icon: n.readAt ? <FileSearchOutlined /> : <BellOutlined />, label: n.title })) }}>
              <Badge count={unread} size="small"><Button type="text" shape="circle" icon={<BellOutlined />} /></Badge>
            </Dropdown>
            <Dropdown menu={{ items: [{ key: 'profile', label: '个人资料' }, { type: 'divider' }, { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: () => { logout(); navigate('/login'); } }] }}>
              <Space className="user-chip"><Avatar className="user-avatar">{user?.realName?.slice(0, 1)}</Avatar><div className="user-copy"><div className="user-name">{user?.realName}</div><div className="user-role">{roleLabels[user?.roleCode ?? ''] ?? '项目成员'}</div></div></Space>
            </Dropdown>
          </Space>
        </Header>
        <Content><Outlet /></Content>
        <FloatingAiAssistant />
      </Layout>
    </Layout>
  );
}
